import React, { useMemo } from 'react';

import {
  alpha,
  Box,
  Paper,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { isEmpty } from 'es-toolkit/compat';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  PERFORMANCE_STATUS,
  PERFORMANCE_STATUS_CONFIG,
  PERFORMANCE_STATUS_SNAKE_CASE_MAP,
} from './constants';
import { PerformanceMonitoringCustomTooltip } from './PerformanceMonitoringCustomTooltip';
import {
  fillMissingRangeDataWithZerosAndComputeTotals,
  formatRangeLabel,
} from './utils';

import { useGetSBPerformanceMonitoringQuery } from '@/api/endpoints/starvationBidder';
import { useColorTokens } from '@/app/themes';
import MapError from '@/features/offlineSalesOptimizer/components/mapUiStates/MapError';
import MapNoData from '@/features/offlineSalesOptimizer/components/mapUiStates/MapNoData';
import { usePlatform } from '@/features/users';
import { useGlobalFilterKeys } from '@/shared/hooks';

export const PerformanceMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colorTokens = useColorTokens();
  const platform = usePlatform();
  const isDV360 = platform === 'DV360';
  const entityLabel = isDV360 ? 'Insertion Orders' : 'Campaigns';

  const { globalFilterKeys, isGlobalFilterReady } = useGlobalFilterKeys();
  const {
    data: rawData,
    isFetching,
    isError,
    refetch: refetchData,
  } = useGetSBPerformanceMonitoringQuery(globalFilterKeys, {
    skip: !isGlobalFilterReady,
  });

  const { data, totalCampaigns } = useMemo(
    () => fillMissingRangeDataWithZerosAndComputeTotals(rawData),
    [rawData]
  );

  const renderTick = ({
    x,
    y,
    payload,
  }: {
    x: number;
    y: number;
    payload?: { value: number };
  }) => {
    let tickColor = theme.palette.grey[500];
    const value = payload?.value ?? 0;

    if (value < 0) {
      tickColor = colorTokens.green[200];
    } else if (value > 0) {
      tickColor = colorTokens.red[200];
    }

    return (
      <text
        x={x}
        y={y}
        dy={16}
        fill={tickColor}
        fontSize={12}
        textAnchor='middle'
      >
        {formatRangeLabel(value)}
      </text>
    );
  };

  const handleClick = (performanceStatus: PERFORMANCE_STATUS) => {
    void navigate({
      to: '/algorithm-tools/starvation-bidder',
      search: (prev) => ({
        ...prev,
        starve: 'diag',
        diag: 'detailedDiagnostics' as const,
        performanceStatus: PERFORMANCE_STATUS_SNAKE_CASE_MAP[performanceStatus],
      }),
    });
  };

  return (
    <Paper
      elevation={0}
      sx={[
        (theme) => ({
          height: '100%',
          px: 2.25,
          py: 3,
          border: 1,
          borderColor: 'divider',
          boxShadow: theme.customShadows.sm,
        }),
        (theme) =>
          theme.applyStyles('dark', {
            border: 0,
            boxShadow: 'none',
          }),
      ]}
    >
      <Box display='flex' justifyContent='space-between'>
        <Box display='flex' flexDirection='column'>
          <Typography component='h2' color='text.tertiary'>
            Starvation Bidder Performance Monitoring
          </Typography>
          <Typography
            variant='body3'
            component='p'
            color='text.secondary'
            pt={0.5}
          >
            {entityLabel} with their respective starvation status and spend
            forecast buckets. Click on the bars to view detailed diagnostics.
          </Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            gap: 1,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (theme) => theme.customShadows.sm,
          }}
        >
          <Typography variant='caption'>No. of {entityLabel}</Typography>
          <Typography variant='body0' sx={{ fontWeight: 700 }}>
            {isFetching ? (
              <Skeleton
                variant='rounded'
                width={25}
                height={16}
                sx={{ borderRadius: 2 }}
              />
            ) : (
              totalCampaigns
            )}
          </Typography>
        </Paper>
      </Box>
      <Box pt={1} height={450}>
        {isFetching && (
          <Skeleton
            variant='rounded'
            height='100%'
            data-testid='loading-skeleton'
          />
        )}
        {isError && (
          <Box
            width='100%'
            height='100%'
            display='flex'
            alignItems='center'
            justifyContent='center'
          >
            <MapError
              handleReload={() => {
                void refetchData();
              }}
            />
          </Box>
        )}
        {!isFetching && !isError && isEmpty(data) && (
          <Box
            width='100%'
            height='100%'
            display='flex'
            alignItems='center'
            justifyContent='center'
          >
            <MapNoData
              subHeadingText={`There is no data available for the selected advertiser`}
            />
          </Box>
        )}
        {!isFetching && !isError && !isEmpty(data) && (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={data}
              margin={{
                top: 40,
                right: 20,
                left: 20,
                bottom: 0,
              }}
              maxBarSize={40}
            >
              <CartesianGrid
                strokeDasharray='0 0'
                horizontal={true}
                vertical={false}
                strokeWidth={1}
                stroke={
                  theme.palette.mode === 'light'
                    ? theme.palette.grey[100]
                    : theme.palette.grey[25]
                }
              />

              <XAxis
                dataKey='range'
                tick={renderTick}
                axisLine={{
                  stroke: theme.palette.text.secondary,
                  strokeWidth: 0.1,
                }}
                tickLine={false}
              />

              <YAxis
                hide
                allowDataOverflow
                domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.2)]}
              />

              <Tooltip
                content={<PerformanceMonitoringCustomTooltip />}
                cursor={{
                  fill: alpha(theme.palette.grey[200], 0.4),
                }}
              />
              <ReferenceLine
                x={0}
                stroke={theme.palette.text.secondary}
                strokeWidth={0.5}
                label={{
                  value: 'KPI Goal',
                  position: 'top',
                  fill: theme.palette.text.secondary,
                  fontSize: 14,
                  fontWeight: 800,
                  offset: 20,
                }}
              />
              {Object.values(PERFORMANCE_STATUS).map((status) => (
                <Bar
                  dataKey={status}
                  stackId='a'
                  fill={colorTokens.performanceStatusColors[status]}
                  onClick={() => {
                    handleClick(status);
                  }}
                  key={status}
                />
              ))}
              <Legend
                iconType='circle'
                iconSize={8}
                formatter={(value: PERFORMANCE_STATUS) => {
                  const config = PERFORMANCE_STATUS_CONFIG[value];

                  return (
                    <Box
                      display='inline-flex'
                      flexDirection='column'
                      alignItems='flex-start'
                      m={1}
                    >
                      <Typography
                        variant='body3'
                        component='span'
                        color='textPrimary'
                      >
                        Starvation:
                        <span
                          style={{
                            color:
                              config.starvation?.intent === 'success'
                                ? colorTokens.green[200]
                                : colorTokens.red[200],
                            fontWeight: 700,
                          }}
                        >
                          {config.starvation?.label}
                        </span>
                      </Typography>
                      <Typography
                        variant='body3'
                        component='span'
                        color='textPrimary'
                      >
                        Spend Forecast:
                        <span
                          style={{
                            color:
                              config.spendForecast?.intent === 'success'
                                ? colorTokens.green[200]
                                : colorTokens.red[200],
                            fontWeight: 700,
                          }}
                        >
                          {config.spendForecast?.label}
                        </span>
                      </Typography>
                    </Box>
                  );
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
};
